import PropTypes from 'prop-types';
import { Link as RouterLink, matchPath, useLocation } from 'react-router-dom';
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
// material
import { Box, Fab, Stack } from '@material-ui/core';
import Avatar, { genConfig } from 'react-nice-avatar';
// routes
import { PATH_DASHBOARD } from '../../routes/paths';
// components
import { MHidden } from '../../components/@material-extend';
import SvgIconStyle from '../../components/SvgIconStyle';
// hooks
import useLocales from '../../hooks/useLocales';
import useAuth from '../../hooks/useAuth';
// utils
import { convertUrlToObj } from '../../utils/niceAvatar';

// ----------------------------------------------------------------------

const BAR_HEIGHT = 64;

// Balanced 2-and-2 around the center FAB, avatar always last on the right.
const LEFT_ITEMS = [
  { titleKey: 'nav.home', path: PATH_DASHBOARD.root, icon: '/static/icons/navbar/ic_home.png', end: true },
  { titleKey: 'nav.myDecks', path: PATH_DASHBOARD.decks.root, icon: '/static/icons/navbar/ic_deck.png' },
];

const RIGHT_ITEMS = [
  { titleKey: 'nav.progress', path: PATH_DASHBOARD.progress, icon: '/static/icons/navbar/ic_progress.png' },
];

// ----------------------------------------------------------------------

BottomNavItem.propTypes = {
  titleKey: PropTypes.string.isRequired,
  path: PropTypes.string.isRequired,
  icon: PropTypes.string.isRequired,
  end: PropTypes.bool,
};

BottomNavItem.defaultProps = {
  end: false,
};

function BottomNavItem({ titleKey, path, icon, end }) {
  const { translate } = useLocales();
  const title = translate(titleKey);
  const { pathname } = useLocation();
  const isActive = !!matchPath({ path, end }, pathname);
  const isSvg = icon.endsWith('.svg');

  return (
    <Box
      component={RouterLink}
      to={path}
      aria-label={title}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 52,
        height: 44,
        borderRadius: 1.5,
        textDecoration: 'none',
        bgcolor: isActive ? 'primary.main' : 'transparent',
      }}
    >
      <Box sx={{ width: 22, height: 22, color: isActive ? 'grey.800' : 'text.disabled' }}>
        {isSvg ? (
          <SvgIconStyle src={icon} sx={{ width: '100%', height: '100%' }} />
        ) : (
          <img src={icon} alt={title} style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
        )}
      </Box>
    </Box>
  );
}

// Renders the user's actual avatar instead of a static icon — BottomNavItem above only knows
// how to render an image path, so this is a sibling rather than a branch inside it.
function BottomNavAvatarItem({ path, titleKey }) {
  const { translate } = useLocales();
  const { user } = useAuth();
  const { pathname } = useLocation();
  const isActive = !!matchPath({ path }, pathname);
  const config = genConfig(convertUrlToObj(user.photoURL));

  return (
    <Box
      component={RouterLink}
      to={path}
      aria-label={translate(titleKey)}
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 52,
        height: 44,
        borderRadius: 1.5,
        textDecoration: 'none',
        bgcolor: isActive ? 'primary.main' : 'transparent',
      }}
    >
      <Avatar style={{ width: 26, height: 26 }} {...config} />
    </Box>
  );
}

BottomNavAvatarItem.propTypes = {
  path: PropTypes.string.isRequired,
  titleKey: PropTypes.string.isRequired,
};

export default function MobileBottomNav() {
  const { translate } = useLocales();
  return (
    <MHidden width="lgUp">
      <Box
        sx={{
          position: 'fixed',
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: (theme) => theme.zIndex.appBar,
          height: BAR_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 3,
          bgcolor: 'background.paper',
          borderTop: (theme) => `2.5px solid ${theme.palette.grey[800]}`,
        }}
      >
        <Stack direction="row" spacing={1}>
          {LEFT_ITEMS.map((item) => (
            <BottomNavItem key={item.titleKey} {...item} />
          ))}
        </Stack>

        <Fab
          component={RouterLink}
          to={PATH_DASHBOARD.decks.import}
          aria-label={translate('nav.addDeck')}
          size="medium"
          sx={{
            position: 'absolute',
            left: '50%',
            top: 0,
            transform: 'translate(-50%, -55%)',
            border: (theme) => `2.5px solid ${theme.palette.grey[800]}`,
            boxShadow: (theme) => `3px 4px 0 ${theme.palette.grey[800]}`,
            '&:hover': { boxShadow: (theme) => `3px 4px 0 ${theme.palette.grey[800]}` },
          }}
        >
          <Icon icon={plusFill} width={24} height={24} />
        </Fab>

        <Stack direction="row" spacing={1}>
          {RIGHT_ITEMS.map((item) => (
            <BottomNavItem key={item.titleKey} {...item} />
          ))}
          <BottomNavAvatarItem path={PATH_DASHBOARD.profile} titleKey="nav.profile" />
        </Stack>
      </Box>
    </MHidden>
  );
}
